import React, { useState } from "react";
import {
    Container,
    Typography,
    TextField,
    Button,
    CssBaseline,
    Grid,
    Link,
    CircularProgress,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { API, Auth } from "aws-amplify";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import VerificationForm from "./VerificationForm";
import Logo from "../../assets/logo.jpg";
import { getCorporateUser } from "../Corporate/CorporateQueries";
import { login as jwtLogin } from "../../services/authApi";

const useStyles = makeStyles({
    root: {
        display: "flex",
        height: "100vh",
        justifyContent: "center",
        alignItems: "center",
        background: "linear-gradient(to right, #0a0068, #cc4049)",
    },
    content: {
        textAlign: "center",
        color: "#ffffff",
    },
    loginForm: {
        background: "#fff",
        padding: "40px",
        height: "auto",
        borderRadius: "8px",
        boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
    },
    submit: {
        marginTop: "16px",
        backgroundColor: "#0a0068",
        color: "#ffffff",
    },
    errorMessage: {
        color: "red",
        textAlign: "center",
    },
});

const api_name = process.env.REACT_APP_STAGE === "DEV" ? process.env.REACT_APP_API_NAME_DEV : process.env.REACT_APP_API_NAME_PROD;
const isCorporateMode = process.env.REACT_APP_CORPORATE_MODE === 'true';

const LoginPage = ({ onRefresh }) => {
    const classes = useStyles();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmationCode, setConfirmationCode] = useState("");
    const [resetPassword, setResetPassword] = useState(false);
    const [resetStage, setResetStage] = useState(1);
    const [showFirstTime, setShowFirstTime] = useState(false);
    const [userChallenge, setUserChallenge] = useState(null);
    const [emailConfirmed, setEmailConfirmed] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const navigate = useNavigate();

    // Helper: normalize DynamoDB/flat responses
    const pickVal = (obj, key) => {
        const v = obj?.[key];
        if (v && typeof v === 'object' && 'S' in v) return v.S || '';
        return v ?? '';
    };

    // Corporate-first data loader and validation
    const fetchAndStoreCorporateData = async () => {
        try {
            const corp = await getCorporateUser(email);
            const raw = Array.isArray(corp?.users) ? corp.users[0] : (corp?.user || corp?.Item || corp);
            const corpEmail = pickVal(raw, 'email') || email;
            const profileStatus = (pickVal(raw, 'profile_status') || 'Active').trim();
            if (!raw || !corpEmail || profileStatus.toLowerCase() !== 'active') {
                throw new Error('Access required: your profile is inactive. Please contact your administrator.');
            }

            const corporateUser = {
                email: corpEmail,
                first_name: pickVal(raw, 'first_name'),
                last_name: pickVal(raw, 'last_name'),
                job_title: pickVal(raw, 'job_title'),
                department: pickVal(raw, 'department'),
                role: pickVal(raw, 'role') || 'Employee',
                internal_experience: pickVal(raw, 'internal_experience'),
                external_experience: pickVal(raw, 'external_experience'),
                reporting_l1: pickVal(raw, 'reporting_l1'),
                reporting_l2: pickVal(raw, 'reporting_l2'),
                work_location: pickVal(raw, 'work_location'),
                company: pickVal(raw, 'company'),
                notes: pickVal(raw, 'notes'),
                profile_status: profileStatus,
                program_code: pickVal(raw, 'program_code'),
                created_by: pickVal(raw, 'created_by'),
                updated_by: pickVal(raw, 'updated_by'),
                created_date: pickVal(raw, 'created_date'),
                updated_date: pickVal(raw, 'updated_date'),
                photo: pickVal(raw, 'photo'),
            };

            // Core localStorage keys
            localStorage.setItem('user', corpEmail);
            try { localStorage.setItem('userEmail', corpEmail); sessionStorage.setItem('userEmail', corpEmail); } catch {}
            localStorage.setItem('corporateUser', JSON.stringify(corporateUser));

            // Compatibility keys used elsewhere in app
            localStorage.setItem('fname', corporateUser.first_name || '');
            localStorage.setItem('lname', corporateUser.last_name || '');
            localStorage.setItem('userRole', corporateUser.role || 'Employee');
            localStorage.setItem('profile_image', corporateUser.photo || '');

            return corporateUser;
        } catch (e) {
            throw e;
        }
    };

    const storeUserData = (user) => {
        localStorage.setItem("user", user.email);
        try { 
            localStorage.setItem("userEmail", user.email); 
            sessionStorage.setItem("userEmail", user.email); 
        } catch(_){}
        localStorage.setItem("userData", JSON.stringify(user));
        localStorage.setItem("fname", user.fname || '');
        localStorage.setItem("lname", user.lname || '');
        localStorage.setItem("userRole", user.user_role || 'User');
        localStorage.setItem("profile_image", user.profile_image || '');
        localStorage.setItem("linkedin_url", user.linkedin_url || '');
        localStorage.setItem("git_url", user.git_url || '');
        localStorage.setItem("experience_level", user.experience_level || '');
    };

    const getDefaultPath = (role) => {
        console.log('[Login] getDefaultPath called with role:', role);
        console.log('[Login] isCorporateMode:', isCorporateMode);
        
        // In corporate mode, route based on role
        if (isCorporateMode) {
            // Check if user is a manager/lead/HR
            const isManager = role && (
                role.includes('Team Lead') || 
                role.includes('Project Manager') || 
                role.includes('Program Manager') ||
                role.includes('HR')
            );
            
            console.log('[Login] isManager:', isManager);
            console.log('[Login] Navigating to:', isManager ? '/ManagerReviews' : '/Reviews');
            
            // Managers go to ManagerReviews, employees go to Reviews
            return navigate(isManager ? '/ManagerReviews' : '/Reviews');
        }
        // Non-corporate fallback behavior
        switch (role) {
            case 'Mentor':
                return navigate("/Dashboard"); // Mentors start at Dashboard
            case 'Client':
                return navigate('/Dashboard'); // Clients start at Dashboard
            case 'User':
                return navigate('/AssessmentIndex'); // Users start at Assessments
            default:
                return navigate('/Dashboard'); // Fallback to Dashboard
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setIsLoading(true);
        setErrorMessage("");

        try {
            // Step 1: Authenticate with Cognito first
            const cognitoUser = await Auth.signIn(email, password);
            
            // Handle password change challenge
            if (cognitoUser.challengeName === "NEW_PASSWORD_REQUIRED") {
                setUserChallenge(cognitoUser);
                setIsLoading(false);
                return;
            }

            // Step 2: Get JWT tokens from backend
            const loginData = await jwtLogin(email, password, isCorporateMode);
            
            // Login successful, tokens are automatically stored by jwtLogin
            const user = loginData.user;
            
            if (!user) {
                throw new Error('Invalid login response');
            }

            // Store user data
            storeUserData(user);

            // If corporate mode, also fetch and cache corporate user profile
            if (isCorporateMode) {
                try {
                    await fetchAndStoreCorporateData();
                } catch (e) {
                    console.error('[Login] Corporate validation failed:', e);
                    setErrorMessage(e.message || 'Corporate access validation failed. Please try again or contact support.');
                    return;
                }
            }

            onRefresh();
            
            // Get the role from storage after it's been set
            const userRole = localStorage.getItem('userRole') || user.user_role || 'User';
            console.log('[Login] User role for navigation:', userRole);
            getDefaultPath(userRole);
            
        } catch (err2) {
            console.error('Login error:', err2);
            
            // Check if it's an account disabled error (403)
            const errorStatus = err2?.response?.status || err2?.status || 0;
            const errorMessage = err2?.response?.data?.error || err2?.message || err2?.error;
            
            if (errorStatus === 403 || (errorMessage && errorMessage.includes('disabled'))) {
                setErrorMessage("Your account access has been disabled. Please contact your administrator.");
            }
            // Handle specific Cognito errors
            else if (err2.code === "UserNotConfirmedException") {
                setShowFirstTime(true);
                setErrorMessage("Email not confirmed. Please confirm below.");
            } else if (err2.code === "NotAuthorizedException") {
                setErrorMessage("Incorrect email or password. Please try again.");
            } else {
                setErrorMessage(errorMessage || "Login failed. Please check your credentials and try again.");
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleNewPassword = async (event) => {
        event.preventDefault();
        setIsLoading(true);
        setErrorMessage("");
        setSuccessMessage("");
        try {
            await Auth.completeNewPassword(userChallenge, newPassword);
            setSuccessMessage("Password updated. Please log in again.");
            setUserChallenge(null);
            setPassword("");
            setNewPassword("");
        } catch (err) {
            setErrorMessage(err.message || "Failed to set new password");
        } finally {
            setIsLoading(false);
        }
    };

    const handleConfirmEmail = async (code, emailParam) => {
        setIsLoading(true);
        setErrorMessage("");
        try {
            await Auth.confirmSignUp(emailParam, code);
            setEmailConfirmed(true);
        } catch (err) {
            setErrorMessage(err.message || "Confirmation failed");
        } finally {
            setIsLoading(false);
        }
    };

    const handleResendCode = async (emailParam) => {
        setErrorMessage("");
        setSuccessMessage("");
        try {
            await Auth.resendSignUp(emailParam);
            setSuccessMessage("Confirmation code resent to your email.");
            setEmail(emailParam);
        } catch (err) {
            setErrorMessage(err.message || "Failed to resend code");
        }
    };

    const handleResetPasswordRequest = async () => {
        setIsLoading(true);
        setErrorMessage("");
        setSuccessMessage("");
        try {
            await Auth.forgotPassword(email);
            setSuccessMessage("Verification code sent to your email.");
            setResetStage(2);
        } catch (err) {
            setErrorMessage(err.message || "Failed to request password reset");
        } finally {
            setIsLoading(false);
        }
    };

    const handleResetPasswordConfirm = async () => {
        setIsLoading(true);
        setErrorMessage("");
        setSuccessMessage("");
        try {
            await Auth.forgotPasswordSubmit(email, confirmationCode, newPassword);
            setSuccessMessage("Password reset successful. You can now login.");
            setResetPassword(false);
            setResetStage(1);
            setConfirmationCode("");
            setNewPassword("");
        } catch (err) {
            setErrorMessage(err.message || "Failed to reset password");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className={classes.root}>
            <CssBaseline />
            <Grid container justifyContent="center" alignItems="center" spacing={2}>
                <Grid item xs={12} md={6}>
                    <Container maxWidth="sm" className={classes.content}>
                        <Typography variant="h4" className={classes.typingText}>Welcome to <span style={{ fontWeight: 900 }}>Career Excellence</span></Typography>
                    </Container>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Container maxWidth="xs" className={classes.content}>
                        <div className={classes.loginForm}>
                            <img src={Logo} alt="Logo" style={{ height: '75px', marginBottom: '20px' }} />
                            <Typography
                                component="h1"
                                variant="h6"
                                align="center"
                                style={{
                                    color: "#000000",
                                    marginBottom: "20px",
                                    fontWeight: 700,
                                }}
                            >
                                {showFirstTime
                                    ? "Confirm Your Email"
                                    : resetPassword
                                        ? "Reset Your Password"
                                        : "Login to your Account"}
                            </Typography>

                            {/* ==== LOGIN / NEW PASSWORD ==== */}
                            {!showFirstTime && !resetPassword && (
                                <form onSubmit={userChallenge ? handleNewPassword : handleSubmit}>
                                    <TextField
                                        fullWidth
                                        margin="normal"
                                        label="Email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        required
                                    />
                                    <TextField
                                        fullWidth
                                        margin="normal"
                                        label={userChallenge ? "New Password" : "Password"}
                                        type="password"
                                        value={userChallenge ? newPassword : password}
                                        onChange={(e) =>
                                            userChallenge
                                                ? setNewPassword(e.target.value)
                                                : setPassword(e.target.value)
                                        }
                                        InputLabelProps={{ shrink: true }}
                                        required
                                    />
                                    {successMessage && (
                                        <Typography variant="body2" style={{ color: "green", textAlign: "center", marginTop: "8px" }}>
                                            {successMessage}
                                        </Typography>
                                    )}
                                    <Typography variant="body2" className={classes.errorMessage}>
                                        {errorMessage}
                                    </Typography>
                                    <Button
                                        type="submit"
                                        fullWidth
                                        variant="contained"
                                        className={classes.submit}
                                        disabled={isLoading}
                                        startIcon={
                                            isLoading ? (
                                                <CircularProgress size={20} color="inherit" />
                                            ) : null
                                        }
                                    >
                                        {isLoading ? "Signing In..." : "Sign In"}
                                    </Button>

                                    <Grid container justifyContent="space-between" sx={{ mt: 2 }}>
                                        {/* <Grid item>
                                            <Link component={RouterLink} to="/signup" variant="body2">
                                                Don't have an account? Sign Up
                                            </Link>
                                        </Grid> */}
                                        <Grid item sx={{ mt: 2 }}>
                                            <Link
                                                component="button"
                                                variant="body2"
                                                onClick={() => setResetPassword(true)}
                                            >
                                                Forgot Password?
                                            </Link>
                                        </Grid>
                                        {/* <Grid item sx={{ mt: 2 }}>
                                            <Link
                                                component="button"
                                                variant="body2"
                                                onClick={() => setShowFirstTime(true)}
                                            >
                                                First time login? Confirm your email
                                            </Link>
                                        </Grid> */}
                                    </Grid>
                                </form>
                            )}

                            {/* ==== CONFIRM EMAIL ==== */}
                            {showFirstTime && (
                                <VerificationForm
                                    email={email}
                                    onConfirm={handleConfirmEmail}
                                    onResend={handleResendCode}
                                    errorMessage={errorMessage}
                                    showEmailField={true}
                                    loading={isLoading}
                                    success={emailConfirmed}
                                />
                            )}

                            {/* ==== RESET PASSWORD ==== */}
                            {resetPassword && (
                                <form
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                        resetStage === 1
                                            ? handleResetPasswordRequest()
                                            : handleResetPasswordConfirm();
                                    }}
                                >
                                    <TextField
                                        fullWidth
                                        margin="normal"
                                        label="Email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                    />
                                    {resetStage === 2 && (
                                        <>
                                            <TextField
                                                fullWidth
                                                margin="normal"
                                                label="Verification Code"
                                                value={confirmationCode}
                                                onChange={(e) => setConfirmationCode(e.target.value)}
                                                required
                                            />
                                            <TextField
                                                fullWidth
                                                margin="normal"
                                                label="New Password"
                                                type="password"
                                                value={newPassword}
                                                onChange={(e) => setNewPassword(e.target.value)}
                                                required
                                            />
                                        </>
                                    )}
                                    <Typography variant="body2" className={classes.errorMessage}>
                                        {errorMessage}
                                    </Typography>
                                    <Button
                                        type="submit"
                                        fullWidth
                                        variant="contained"
                                        className={classes.submit}
                                        disabled={isLoading}
                                        startIcon={
                                            isLoading ? (
                                                <CircularProgress size={20} color="inherit" />
                                            ) : null
                                        }
                                    >
                                        {isLoading
                                            ? "Please wait..."
                                            : resetStage === 1
                                                ? "Send Code"
                                                : "Reset Password"}
                                    </Button>

                                    <Grid container justifyContent="center" sx={{ mt: 2 }}>
                                        <Grid item>
                                            <Link
                                                component="button"
                                                variant="body2"
                                                onClick={() => setResetPassword(false)}
                                            >
                                                Back to Sign In
                                            </Link>
                                        </Grid>
                                    </Grid>
                                </form>
                            )}
                        </div>
                    </Container>
                </Grid>
            </Grid>
        </div>
    );
};

export default LoginPage;
